import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, Phone, CaretDown, ShoppingCart, User, Trash, Minus, Plus } from "@phosphor-icons/react";
import { NAV, BUSINESS } from "../../lib/data";
import { useCart, unitPrice, maxFor } from "../../lib/cart";
import { useAuth } from "../../lib/auth";

const kebab = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const DesktopItem = ({ n, pathname }) => {
  const [open, setOpen] = useState(false);
  const active = pathname === n.to;
  const base = `px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
    active ? "text-navy-900" : "text-slate-600 hover:text-navy-900"
  }`;

  if (!n.children) {
    return (
      <Link to={n.to} data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`} className={base}>
        {n.label}
      </Link>
    );
  }

  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link
        to={n.to}
        data-testid={`nav-${n.to.slice(1)}`}
        className={`${base} inline-flex items-center gap-1`}
      >
        {n.label}
        <CaretDown size={13} weight="bold" className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </Link>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute left-0 top-full pt-3 w-72"
          >
            <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_16px_50px_rgb(10,17,40,0.14)] p-2">
              {n.children.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  data-testid={`nav-${kebab(c.label)}`}
                  className="block px-4 py-3 rounded-xl text-sm font-semibold text-navy-900 hover:bg-slate-50 transition-colors"
                >
                  {c.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { count } = useCart();
  const { user } = useAuth();
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => setCartOpen(false), [pathname]);
  useEffect(() => {
    const openCart = () => setCartOpen(true);
    window.addEventListener("amwap-open-cart", openCart);
    return () => window.removeEventListener("amwap-open-cart", openCart);
  }, []);

  return (
    <>
      <header
        data-testid="site-navbar"
        className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm"
      >
        <nav className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <span className="w-12 h-12 rounded-full bg-white shadow-sm ring-1 ring-slate-200 grid place-items-center overflow-hidden shrink-0">
              <img src={BUSINESS.logo} alt="A Man With A Plan Handyman Services logo" className="w-full h-full object-contain p-0.5" />
            </span>
            <span className="font-display font-extrabold text-navy-900 text-lg tracking-tight leading-none">
              A Man With A Plan
              <span className="block text-[10px] font-sans font-semibold tracking-[0.18em] text-slate-500 uppercase mt-0.5">
                Handyman Services
              </span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <DesktopItem key={n.label} n={n} pathname={pathname} />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/account"
              data-testid="nav-account"
              aria-label="Account"
              className="w-11 h-11 grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-slate-100 transition-colors"
              title={user ? user.email : "Sign in"}
            >
              <User size={20} weight={user ? "fill" : "regular"} />
            </Link>
            <button
              onClick={() => setCartOpen(true)}
              data-testid="nav-cart"
              aria-label="Cart"
              className="relative w-11 h-11 grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-slate-100 transition-colors"
            >
              <ShoppingCart size={20} weight="regular" />
              {count > 0 && (
                <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 grid place-items-center rounded-full bg-navy-900 text-white text-[11px] font-bold">
                  {count}
                </span>
              )}
            </button>
            <a
              href={BUSINESS.phoneHref}
              data-testid="nav-call-now"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-5 py-2.5 text-sm font-bold hover:bg-navy-600 transition-colors"
            >
              <Phone weight="fill" size={16} />
              {BUSINESS.phone}
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden w-11 h-11 grid place-items-center rounded-full border border-slate-300 text-navy-900"
              aria-label="Open menu"
            >
              <List size={22} />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-navy-900 text-white flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="h-[72px] px-5 flex items-center justify-between border-b border-white/10">
              <span className="flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-full bg-white grid place-items-center overflow-hidden">
                  <img src={BUSINESS.logo} alt="AMWAP logo" className="w-full h-full object-contain p-0.5" />
                </span>
                <span className="font-display font-extrabold text-lg">AMWAP</span>
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="w-11 h-11 grid place-items-center rounded-full border border-white/20"
                aria-label="Close menu"
              >
                <X size={22} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto flex flex-col justify-center px-8 gap-1">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <Link
                    to={n.to}
                    data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                    className="font-display font-extrabold text-3xl tracking-tight py-1.5 block"
                  >
                    {n.label}
                  </Link>
                  {n.children && (
                    <div className="pl-4 pb-2 flex flex-col gap-1">
                      {n.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          data-testid={`mobile-nav-${kebab(c.label)}`}
                          className="text-slate-300 font-semibold text-base py-1"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
            <a
              href={BUSINESS.phoneHref}
              className="m-8 rounded-full bg-white text-navy-900 px-6 py-4 text-center font-bold flex items-center justify-center gap-2"
            >
              <Phone weight="fill" size={18} /> {BUSINESS.phone}
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
};

const CartDrawer = ({ open, onClose }) => {
  const { items, setQty, removeItem, subtotal, count } = useCart();
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] bg-navy-900/50 backdrop-blur-sm"
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.28 }}
            className="fixed top-0 right-0 z-[90] h-full w-full max-w-[420px] bg-white shadow-2xl flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-[72px] border-b border-slate-200 shrink-0">
              <h3 className="font-display font-black text-lg text-navy-900 flex items-center gap-2"><ShoppingCart size={20} weight="fill" /> Your cart ({count})</h3>
              <button onClick={onClose} data-testid="cart-drawer-close" aria-label="Close" className="w-9 h-9 grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-slate-100"><X size={18} /></button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 grid place-items-center text-center px-6" data-testid="cart-drawer-empty">
                <div>
                  <ShoppingCart size={40} weight="thin" className="text-navy-300 mx-auto" />
                  <p className="text-slate-500 mt-3">Your cart is empty.</p>
                  <Link to="/supply" onClick={onClose} className="inline-block mt-5 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600 transition-colors">Browse the catalog</Link>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                  {items.map((it, i) => {
                    const ppu = unitPrice(it);
                    const cap = maxFor(it);
                    const atMax = Number.isFinite(cap) && it.quantity >= cap;
                    return (
                      <div key={it.product_id} data-testid={`drawer-item-${i}`} className="flex items-center gap-3 border border-slate-200 rounded-xl p-3">
                        <div className="w-14 h-14 rounded-lg bg-slate-50 border border-slate-200 grid place-items-center overflow-hidden shrink-0">
                          {it.image ? <img src={it.image} alt="" className="w-full h-full object-contain p-1" /> : <ShoppingCart size={18} className="text-slate-300" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <Link to={`/supply/${it.product_id}`} onClick={onClose} className="text-sm font-semibold text-navy-900 hover:underline line-clamp-2">{it.name}</Link>
                          {atMax && <p data-testid={`drawer-max-note-${i}`} className="text-[11px] font-semibold text-orange-600 mt-0.5">Only {cap} in stock</p>}
                          <div className="flex items-center gap-2 mt-1.5">
                            <div className="inline-flex items-center rounded-full border border-slate-300 overflow-hidden">
                              <button onClick={() => setQty(it.product_id, it.quantity - 1)} data-testid={`drawer-minus-${i}`} aria-label="Decrease" className="w-7 h-7 grid place-items-center text-navy-900 hover:bg-slate-100"><Minus size={12} weight="bold" /></button>
                              <span className="w-7 text-center text-sm font-bold text-navy-900">{it.quantity}</span>
                              <button onClick={() => setQty(it.product_id, it.quantity + 1)} disabled={atMax} data-testid={`drawer-plus-${i}`} aria-label="Increase" className="w-7 h-7 grid place-items-center text-navy-900 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"><Plus size={12} weight="bold" /></button>
                            </div>
                            <span className="text-sm font-display font-black text-navy-900 ml-auto">${(ppu * it.quantity).toFixed(2)}</span>
                            <button onClick={() => removeItem(it.product_id)} data-testid={`drawer-remove-${i}`} aria-label="Remove" className="w-7 h-7 grid place-items-center rounded-full text-red-500 hover:bg-red-50"><Trash size={15} /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-slate-200 p-5 shrink-0">
                  <div className="flex justify-between font-display font-black text-navy-900 mb-3"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  <Link to="/supply/cart" onClick={onClose} data-testid="drawer-view-cart" className="block w-full text-center rounded-full border border-slate-300 text-navy-900 py-3 font-bold hover:bg-slate-100 transition-colors mb-2">View cart & checkout</Link>
                  <Link to="/supply" onClick={onClose} className="block text-center text-sm text-slate-500 hover:text-navy-900">Continue shopping</Link>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
